import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetJobTitles } from '../../hooks/jobTitles/useGetJobTitles';
import { useDeleteJobTitle } from '../../hooks/jobTitles/useDeleteJobTitle';
import { useDeleteAllJobTitles } from '../../hooks/jobTitles/useDeleteAllJobTitles';
import { useForceDeleteJobTitle } from '../../hooks/jobTitles/useForceDeleteJobTitle';
import { useForceDeleteAllJobTitles } from '../../hooks/jobTitles/useForceDeleteAllJobTitles';
import { useRestoreJobTitle } from '../../hooks/jobTitles/useRestoreJobTitle';
import { useRestoreAllJobTitles } from '../../hooks/jobTitles/useRestoreAllJobTitles';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import JobTitlesTableColumns from './Table/JobTitlesTableColumns';
import JobTitlesTableRow from './Table/JobTitlesTableRow';

const JobTitlesTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const [filterDeptId, setFilterDeptId] = useState('');

    const perms = useMemo(() => ({
        list: has('job_titles_list'),
        create: has('job_titles_create'),
        edit: has('job_titles_edit'),
        delete: has('job_titles_delete'),
        archive_list: has('job_titles_archive_list'),
        restore: has('job_titles_restore'),
        force_delete: has('job_titles_force_delete'),
    }), [has]);

    const { deleteJobTitle, isDelete } = useDeleteJobTitle();
    const { deleteAllJobTitles, isDelete: isDeletingAll } = useDeleteAllJobTitles();
    const { forceDeleteJobTitle } = useForceDeleteJobTitle();
    const { forceDeleteAllJobTitles } = useForceDeleteAllJobTitles();
    const { restoreJobTitle } = useRestoreJobTitle();
    const { restoreAllJobTitles } = useRestoreAllJobTitles();

    const { departments } = useGetDepartments(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds,
        menuAnchorEl, setSearch,
        handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit,
        handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore,
        handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/job-titles',
        deleteSingle: deleteJobTitle,
        deleteAll: deleteAllJobTitles,
        forceDeleteSingle: forceDeleteJobTitle,
        forceDeleteAll: forceDeleteAllJobTitles,
        restoreSingle: restoreJobTitle,
        restoreAll: restoreAllJobTitles,
        isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        department_id: filterDeptId || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterDeptId, isArchive]);

    const { isLoading, error, jobTitles, meta } = useGetJobTitles(page, perPage, filters);

    const renderRow = (params) => (
        <JobTitlesTableRow
            key={params.key || params.item.id}
            item={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={jobTitles.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    select size="small" value={filterDeptId}
                    onChange={(e) => setFilterDeptId(e.target.value)}
                    label={isArabic ? 'فلتر حسب القسم' : 'Filter by Department'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{
                        minWidth: 220,
                        ...(isArabic && {
                            '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                            '& .MuiInputLabel-shrink': { right: 32 },
                            '& .MuiSelect-icon': { left: 7, right: 'unset' },
                            '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                            '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                        }),
                    }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}
                >
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(departments || []).map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                    ))}
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && <ArchiveButton to="/job-titles/archive" />}
                    {isArchive && perms.list && (
                        <ArchiveButton to="/job-titles" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />
                    )}
                </Box>
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف المسميات الوظيفية' : 'المسميات الوظيفية') : (isArchive ? 'Job Titles Archive' : 'Job Titles')}
                subtitle={isArabic ? (isArchive ? 'إدارة المسميات المحذوفة' : 'إدارة المسميات الوظيفية') : (isArchive ? 'Manage deleted job titles' : 'Manage job titles')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة مسمى وظيفي' : 'Add Job Title'}
                onAddClick={() => navigate('/job-titles/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error}
                items={jobTitles} meta={meta}
                page={page} perPage={perPage}
                onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, jobTitles)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={JobTitlesTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد مسميات في الأرشيف' : 'لا توجد مسميات وظيفية'}
                emptyLabelEn={isArchive ? 'No archived job titles' : 'No job titles'}
                loadErrorAr="حدث خطأ أثناء تحميل المسميات الوظيفية"
                loadErrorEn="Failed to load job titles"
            />
        </>
    );
};

export default JobTitlesTable;
